'use client'

import React, { useState } from 'react'
import { cn } from '@/lib/utils'
import { colors, spacing, radius, shadows } from '@/styles/designSystem'
import {
  SURFACE_FG_ATTR,
  surfaceForegroundStyle,
} from '@/lib/design/surfaceForeground'

export type AppCardVariant = 'primary' | 'secondary' | 'flat'

export interface AppCardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: AppCardVariant
  children: React.ReactNode
}

/**
 * Standard card component — use for all card-style containers.
 * PRIMARY: elevated surface with border and shadow; subtle hover elevation.
 * SECONDARY: surface with light border, no shadow.
 * FLAT: no border, no shadow, padding only.
 * Sets light-surface foreground so children inherit navy, not white from a dark parent.
 */
export function AppCard({
  variant = 'primary',
  className,
  children,
  onMouseEnter,
  onMouseLeave,
  ...props
}: AppCardProps) {
  const [hovered, setHovered] = useState(false)

  const base = 'transition-all duration-[150ms] ease-out'

  const variantStyles: Record<AppCardVariant, React.CSSProperties> = {
    primary: {
      background: colors.surface,
      border: `1px solid ${colors.border}`,
      boxShadow: hovered ? shadows.hover : shadows.card,
      padding: spacing.lg,
      borderRadius: radius.lg,
      ...surfaceForegroundStyle.lightElevated,
    },
    secondary: {
      background: colors.surface,
      border: '1px solid #F5F5F7',
      boxShadow: 'none',
      padding: spacing.md,
      borderRadius: radius.lg,
      ...surfaceForegroundStyle.lightElevated,
    },
    flat: {
      background: 'transparent',
      border: 'none',
      boxShadow: 'none',
      padding: spacing.md,
      borderRadius: radius.lg,
      ...surfaceForegroundStyle.lightElevated,
    },
  }

  const style = variantStyles[variant]

  return (
    <div
      className={cn(base, className)}
      data-surface-fg={SURFACE_FG_ATTR.lightElevated}
      style={style}
      onMouseEnter={(e) => {
        setHovered(true)
        onMouseEnter?.(e)
      }}
      onMouseLeave={(e) => {
        setHovered(false)
        onMouseLeave?.(e)
      }}
      {...props}
    >
      {children}
    </div>
  )
}
